import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ChevronRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";

const ONBOARDING_BG = "https://static.prod-images.emergentagent.com/jobs/c1134d5f-8ffa-4e52-be31-05765f8e9179/images/cc97c84e6556ea56d5a7a0d8116ab3cb8215b97596013bb9bdabc9aa26e10a6c.png";

const initialState = {
  name: "", gender: "", date_of_birth: "", height_cm: "", weight_kg: "", blood_group: "",
  mobile: "", emergency_contact: "", waist_cm: "",
  diabetes: false, hypertension: false, cholesterol: false, heart_disease: false, thyroid: false,
  allergies: "", current_medications: "", family_history: "",
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: user.name || f.name,
        gender: user.gender || "",
        date_of_birth: user.date_of_birth || "",
        height_cm: user.height_cm || "",
        weight_kg: user.weight_kg || "",
        blood_group: user.blood_group || "",
        mobile: user.mobile || "",
        emergency_contact: user.emergency_contact || "",
        waist_cm: user.waist_cm || "",
        diabetes: !!user.diabetes, hypertension: !!user.hypertension, cholesterol: !!user.cholesterol,
        heart_disease: !!user.heart_disease, thyroid: !!user.thyroid,
        allergies: user.allergies || "", current_medications: user.current_medications || "",
        family_history: user.family_history || "",
      }));
    }
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      const payload = { ...form,
        height_cm: form.height_cm ? Number(form.height_cm) : undefined,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : undefined,
        waist_cm: form.waist_cm ? Number(form.waist_cm) : undefined,
      };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.put("/profile", payload);
      await refresh();
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:block w-1/3 relative">
        <img src={ONBOARDING_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 p-10 flex flex-col h-full">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white grid place-items-center"><Heart className="w-5 h-5" /></div>
            <span className="font-heading font-bold text-xl text-slate-900">Health Guardian</span>
          </div>
          <div className="mt-auto">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Let's set up your profile</h2>
            <p className="text-slate-600 mt-2">It takes about 2 minutes. This helps us personalize your health insights.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex justify-center p-6 md:p-10 overflow-y-auto">
        <div className="w-full max-w-2xl animate-fade-up">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Step {step} of 2</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">{step === 1 ? "Personal information" : "Medical information"}</h1>
          <p className="text-slate-500 mb-8">{step === 1 ? "Tell us about yourself." : "Help us understand your health context."}</p>

          {step === 1 && (
            <div className="space-y-4 stagger" data-testid="onboarding-step1">
              <Field label="Full name"><Input data-testid="ob-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl bg-slate-50" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Gender">
                  <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger data-testid="ob-gender" className="rounded-xl bg-slate-50"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Date of birth"><Input type="date" data-testid="ob-dob" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="rounded-xl bg-slate-50" /></Field>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field label="Height (cm)"><Input type="number" data-testid="ob-height" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} className="rounded-xl bg-slate-50" /></Field>
                <Field label="Weight (kg)"><Input type="number" data-testid="ob-weight" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="rounded-xl bg-slate-50" /></Field>
                <Field label="Waist (cm)"><Input type="number" data-testid="ob-waist" value={form.waist_cm} onChange={(e) => set("waist_cm", e.target.value)} className="rounded-xl bg-slate-50" /></Field>
              </div>
              <Field label="Blood group">
                <Select value={form.blood_group} onValueChange={(v) => set("blood_group", v)}>
                  <SelectTrigger data-testid="ob-blood" className="rounded-xl bg-slate-50"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Mobile"><Input data-testid="ob-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="rounded-xl bg-slate-50" /></Field>
                <Field label="Emergency contact"><Input data-testid="ob-emergency" value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} className="rounded-xl bg-slate-50" /></Field>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={() => setStep(2)} data-testid="ob-next" className="rounded-full bg-blue-600 hover:bg-blue-700 h-11 px-6">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 stagger" data-testid="onboarding-step2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { k: "diabetes", l: "Diabetes" },
                  { k: "hypertension", l: "Hypertension" },
                  { k: "cholesterol", l: "Cholesterol" },
                  { k: "heart_disease", l: "Heart disease" },
                  { k: "thyroid", l: "Thyroid" },
                ].map(({ k, l }) => (
                  <div key={k} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
                    <span className="text-sm font-medium text-slate-800">{l}</span>
                    <Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`ob-${k}`} />
                  </div>
                ))}
              </div>
              <Field label="Allergies"><Textarea data-testid="ob-allergies" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="e.g. Penicillin, peanuts" className="rounded-xl bg-slate-50 min-h-[64px]" /></Field>
              <Field label="Current medications"><Textarea data-testid="ob-current-meds" value={form.current_medications} onChange={(e) => set("current_medications", e.target.value)} placeholder="List medicines you take regularly" className="rounded-xl bg-slate-50 min-h-[64px]" /></Field>
              <Field label="Family medical history"><Textarea data-testid="ob-family-history" value={form.family_history} onChange={(e) => set("family_history", e.target.value)} placeholder="Any conditions running in the family?" className="rounded-xl bg-slate-50 min-h-[64px]" /></Field>

              {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="onboarding-error">{error}</div>}

              <div className="flex justify-between gap-3 pt-2">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="ob-back" className="rounded-full h-11 px-6">Back</Button>
                <Button onClick={submit} disabled={loading} data-testid="ob-submit" className="rounded-full bg-blue-600 hover:bg-blue-700 h-11 px-6">{loading ? "Saving…" : "Finish setup"}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
